import { Link, useParams } from "react-router-dom";
import { useEmissionsSourceById } from "../api/emissions-sources";
import MainLayout from "../components/MainLayout";
import ContentContainer from "../components/ContentContainer";
import {
    ArrowLeft,
    Globe,
    WarningCircle,
} from "@phosphor-icons/react";
import Typography from "../components/Typography";
import SectionTitle from "../components/SectionTitle";
import Button from "../components/Button";
import { RequireEmissionsSourceEvidenceTable } from "../components/RequiredEvidenceTable";
import { appPaths } from "../Routes";
import IconButton from "../components/IconButton";
import {
    colourToBgClassMap,
    dataStatusIds,
    dataStatusNameToColourMap,
    ConfidenceName,
} from "../constants";
import clsx from "clsx";
import ActionMenu from "../components/ActionMenu";
import DialogBox, { useDialogBoxState } from "../components/DialogBox";
import MakeMeasuredForm from "../components/MakeMeasuredForm/MakeMeasuredForm";
import MakeSecondaryForm from "../components/MakeSecondaryForm/MakeSecondaryForm";
import MakePrimaryForm from "../components/MakePrimaryForm/MakePrimaryForm";
import { isDefined } from "../utils/isDefined";
import { ActionMenuItemOption } from "../components/ActionMenu/ActionMenuOption";
import DataSourceCalculationCards from "../components/DataSourceCalculationFactorCards";

const EmissionsSourceDetailPage = () => {
    const { id } = useParams();
    const makeMeasuredDialogState = useDialogBoxState();
    const makePrimaryDialogState = useDialogBoxState();
    const makeSecondaryDialogState = useDialogBoxState();

    const { emissionsSource, isLoading, refetch } = useEmissionsSourceById(
        Number(id)
    );

    const handleMakeMeasuredFormSubmission = async () => {
        makeMeasuredDialogState.close();
        refetch();
    };

    const handleMakePrimaryFormSubmission = async () => {
        makePrimaryDialogState.close();
        refetch();
    };

    const handleMakeSecondaryFormSubmission = async () => {
        makeSecondaryDialogState.close();
        refetch();
    };

    const buildActionMenuOptions = (id: number, dataStatus: number) => {
        const actionMenuOptions: ActionMenuItemOption[] = [
            {
                label: "Edit emissions source",
                to: appPaths.emissionsSourceEdit(id.toString()),
            },
        ];

        switch (dataStatus) {
            case dataStatusIds.omitted:
            case dataStatusIds.estimated: {
                actionMenuOptions.push({
                    label: "Make measured",
                    onClick: makeMeasuredDialogState.open,
                });
                break;
            }
            case dataStatusIds.measuredSecondary: {
                actionMenuOptions.push({
                    label: "Make primary",
                    onClick: makePrimaryDialogState.open,
                });
                break;
            }
            case dataStatusIds.measuredPrimary: {
                actionMenuOptions.push({
                    label: "Make secondary",
                    onClick: makeSecondaryDialogState.open,
                });
                break;
            }
        }
        return actionMenuOptions;
    };

    return (
        <MainLayout isLoading={isLoading}>
            {!emissionsSource && (
                <ContentContainer className="py-10 text-center">
                    <p>No emissions source found matching this id</p>
                </ContentContainer>
            )}
            {emissionsSource && (
                <ContentContainer className="pt-14 pb-11">
                    <div className="flex sm:flex-row gap-6 items-center pb-5">
                        <div className="flex items-center">
                            <IconButton
                                label="Back"
                                icon={ArrowLeft}
                                iconSize="large"
                                className="mr-3"
                                onClick={() => window.history.back()}
                            />
                            <Typography variant="headline-small" component="h1">
                                {emissionsSource.locationName}{" "}
                                {emissionsSource.name}
                            </Typography>
                        </div>
                        <div className="ml-auto flex items-center gap-2 font-semibold min-w-fit">
                            <Globe weight="light" size={24} />
                            <Typography
                                variant="headline-small"
                                component="span"
                                className="text-deep-ocean-20"
                            >
                                Scope {emissionsSource.scope}
                            </Typography>
                        </div>
                    </div>
                    <SectionTitle title="Emissions Source Information" />
                    <div className="flex justify-between items-center">
                        <span>
                            <span
                                className={clsx(
                                    "text-gray text-center rounded-md text-xs mx-1 py-1 px-3",
                                    "bg-gray-100"
                                )}
                            >
                                {emissionsSource.isMaterial === true
                                    ? "Material"
                                    : "Not Material"}
                            </span>
                            <span
                                className={clsx(
                                    "text-white text-center rounded-md text-xs py-1 px-3",
                                    colourToBgClassMap[
                                        dataStatusNameToColourMap[
                                            emissionsSource.dataStatusName
                                        ]
                                    ]
                                )}
                            >
                                {[
                                    "MeasuredSecondary",
                                    "MeasuredPrimary",
                                ].includes(emissionsSource.dataStatusName) ? (
                                    emissionsSource.dataStatusName ===
                                    "MeasuredSecondary" ? (
                                        "Measured - Secondary"
                                    ) : (
                                        "Measured - Primary"
                                    )
                                ) : (
                                    <>{emissionsSource.dataStatusName}</>
                                )}
                            </span>
                        </span>
                        <ActionMenu
                            label="Actions"
                            options={buildActionMenuOptions(
                                emissionsSource.id,
                                emissionsSource.dataStatusId
                            )}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-6 space-y-0 md-lg:grid-cols-5 justify-between bg-surface-light-1 rounded-xl my-5 p-6">
                        <span>
                            <Typography variant="title-small" component="h2">
                                Location:
                            </Typography>
                            {emissionsSource.locationName}
                        </span>
                        <span>
                            <Typography variant="title-small" component="h2">
                                Unique Reference:
                            </Typography>
                            {emissionsSource.uniqueReference}
                        </span>
                        <span>
                            <Typography variant="title-small" component="h2">
                                Category:
                            </Typography>
                            {emissionsSource.categoryName}
                        </span>
                        <span>
                            <Typography variant="title-small" component="h2">
                                Type:
                            </Typography>
                            {emissionsSource.typeName}
                        </span>
                        <span>
                            <Typography variant="title-small" component="h2">
                                Item:
                            </Typography>
                            {emissionsSource.itemName}
                        </span>
                    </div>
                    <Typography variant="body-small" component="h2">
                        <b>Justification: </b> {emissionsSource.justification}.
                    </Typography>
                    <SectionTitle title="Connected Data Source" />
                    <div className="bg-surface-light-1 rounded-xl p-6">
                        {(!emissionsSource.isMaterial ||
                            emissionsSource.dataStatusId <=
                                dataStatusIds.estimated) && (
                            <div className="flex items-center justify-center gap-2 text-surface-dark">
                                <div>Data source not applicable</div>
                                <WarningCircle size={20} />
                            </div>
                        )}
                        {!emissionsSource.dataSourceId &&
                            emissionsSource.dataStatusId >
                                dataStatusIds.estimated && (
                                <div className="flex items-center justify-center gap-2 text-surface-dark">
                                    <div>No data source found</div>
                                    <WarningCircle size={20} />
                                </div>
                            )}
                        {emissionsSource.dataSourceId && (
                            <>
                                <div className="flex flex-row items-center justify-start">
                                    <Typography
                                        variant="body-large"
                                        component="span"
                                        className="overflow-hidden overflow-ellipsis mr-3"
                                    >
                                        <b>Material:</b>{" "}
                                        {emissionsSource.dataSourceRawMaterial ?? "--"}
                                    </Typography>
                                    <Typography
                                        variant="body-large"
                                        component="span"
                                        className="overflow-hidden overflow-ellipsis"
                                    >
                                        <b>Raw unit:</b>{" "}
                                        {emissionsSource.dataSourceRawUnit ?? "--"}
                                    </Typography>
                                    <div className="flex h-12 items-center min-w-fit flex-1 justify-end">
                                        <Link
                                            to={appPaths.dataSourceDetail(
                                                emissionsSource.dataSourceId.toString()
                                            )}
                                        >
                                            <Button
                                                className="w-full"
                                                variant="outline"
                                            >
                                                View Data Source
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                                {isDefined(
                                    emissionsSource.dataSourceEmissionsFactor
                                ) && (
                                    <DataSourceCalculationCards 
                                        currentConversionFactor={emissionsSource.dataSourceConversionFactor}
                                        currentNormalizedUnitsSuffixHtml={emissionsSource.dataSourceNormalisedUnits as string}
                                        rawUnitsSuffixHtml={emissionsSource.dataSourceRawUnit as string}
                                        confidenceName={emissionsSource.dataSourceEmissionsFactorConfidenceName as ConfidenceName} 
                                        currentEmissionsFactor={emissionsSource.dataSourceEmissionsFactor}
                                        confidenceReason={emissionsSource.dataSourceEmissionsFactorConfidenceReason as string} 
                                    />
                                        
                                )}
                                {!isDefined(
                                    emissionsSource.dataSourceEmissionsFactor
                                ) && (
                                    <div className="flex py-5 justify-center center items-center gap-2 text-surface-dark opacity-60">
                                        <div>No emissions factor set</div>
                                        <WarningCircle size={20} />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <RequireEmissionsSourceEvidenceTable
                        title="Evidence"
                        defaultStatusFilter="All"
                        emissionsSourceId={emissionsSource.id}
                        noEvidenceMessage="No evidence found"
                        hideLinkToEmissionsSource={true}
                    />
                    <DialogBox
                        className="max-w-lg"
                        title="Make Measured"
                        {...makeMeasuredDialogState}
                    >
                        <MakeMeasuredForm
                            emissionsSourceid={emissionsSource.id}
                            onCancel={makeMeasuredDialogState.close}
                            onSubmit={handleMakeMeasuredFormSubmission}
                        />
                    </DialogBox>
                    <DialogBox
                        className="max-w-lg"
                        title="Make Primary"
                        {...makePrimaryDialogState}
                    >
                        <MakePrimaryForm
                            emissionsSourceid={emissionsSource.id}
                            onCancel={makePrimaryDialogState.close}
                            onSubmit={handleMakePrimaryFormSubmission}
                        />
                    </DialogBox>
                    <DialogBox
                        className="max-w-lg"
                        title="Make Secondary"
                        {...makeSecondaryDialogState}
                    >
                        <MakeSecondaryForm
                            emissionsSourceid={emissionsSource.id}
                            onCancel={makeSecondaryDialogState.close}
                            onSubmit={handleMakeSecondaryFormSubmission}
                        />
                    </DialogBox>
                </ContentContainer>
            )}
        </MainLayout>
    );
};

export default EmissionsSourceDetailPage;
